import React, { FC } from 'react';

import { useNavigate } from 'react-router-dom';

import { stringFormatter } from '@nhl/formatter-utilities';

import { PageVars, RecordMap } from '../../../types/RecordCategoriesTypes';
import { Option } from '../../../types/Types';

import RecordTableDataProvider from './recordTable';
import RecordsList from './recordsList';
import { RecordOptions } from './types';

import './styles';

const selectedCategoryValue = (navigate, pageVars, category) => {
    navigate(`/records/${pageVars.mainCategory}/${pageVars.recordType}/${category.value}`);
};

const selectedRecordTypeValue = (navigate, pageVars, recordType) => {
    navigate(`/records/${pageVars.mainCategory}/${recordType.value}`);
};

const getPageVars = (records, page): PageVars => {
    const getPageArray = page.slice(1).split('/');
    // the current record section. ex. => rookie-records
    const mainCategory = getPageArray[1];
    // the current type. ex. => goals
    const recordType = getPageArray[2];
    // Key to access the recordType
    const recordTypeKey = Object.keys(records).find((record) => stringFormatter.toSlug(record) === recordType);
    // the slug. ex. => most-goals-rookie-season
    const recordDescription = getPageArray[3];
    // the current record object
    const currentRecordSubCategory =
        recordDescription &&
        recordTypeKey &&
        records[recordTypeKey].find((subCategory) => subCategory.descriptionKey === recordDescription);

    return {
        mainCategory,
        recordType,
        recordDescription,
        recordTypeKey,
        currentRecordSubCategory,
    };
};

type Props = {
    records: RecordMap;
    templateKey: string;
    page: string;
    pageLevel: number;
    currentSeason: number;
    options?: RecordOptions;
};

const Records: FC<Props> = ({ records, templateKey, page, pageLevel, currentSeason, options }) => {
    const navigate = useNavigate();

    // set view as the records listing page as default ex. /records/skater-records
    let view = (
        <RecordsList
            records={records}
            page={page}
            templateKey={templateKey}
            currentSeason={currentSeason}
            options={options}
        />
    );

    // if page is a records sub-category display the table view ex. /records/skater-records/goals
    if (pageLevel >= 3) {
        const pageVars = getPageVars(records, page);
        const recordTypeKey = pageVars.recordTypeKey || '';

        if (pageVars.recordType === '') {
            return view;
        }

        const recordTypeSelectOptions = () => {
            const options: Option[] = [];
            Object.keys(records).map((record) => {
                options.push({
                    label: record,
                    value: stringFormatter.toSlug(record),
                });
            });

            return options;
        };

        const recordSelectOptions = () => {
            const options: Option[] = [];
            records[recordTypeKey].map((section) => {
                if (section.recordToFranchise[0].restUrl) {
                    options.push({
                        label: section.description,
                        value: section.descriptionKey,
                    });
                }
            });

            return options;
        };

        // if changing the recordType dropdown set default to first record of that type
        const selectedSubCategory = pageVars.currentRecordSubCategory
            ? pageVars.currentRecordSubCategory
            : records[recordTypeKey][0];

        view = (
            <RecordTableDataProvider
                record={selectedSubCategory}
                templateKey={templateKey}
                page={page}
                pageVars={pageVars}
                recordTypeSelectOptions={recordTypeSelectOptions()}
                recordSelectOptions={recordSelectOptions()}
                selectedRecordTypeValue={selectedRecordTypeValue.bind(this, navigate, pageVars)}
                selectedCategoryValue={selectedCategoryValue.bind(this, navigate, pageVars)}
                options={options}
            />
        );
    }

    return view;
};

export default Records;
